import { HyroLoader } from "@hyro-components";
import React, { useEffect, useState } from "react";
import {
  accountManagementErrorSelector,
  accountManagementErrorTextSelector,
  createAccountSelector,
  deleteAccountSelector,
  editAccountSelector,
  getAccountsAdminSelector,
  hasPermissionSelector,
  managementAccountsSelector,
  setAccountManagementErrorSelector
} from "src/stores/selectors/appSelectors";
import AccountEditorDialog from "./AccountEditorDialog";
import AccountEditorDrawer from "./AccountEditorDrawer";
import AddIcon from "@mui/icons-material/Add";
import { DEFAULT_TIMEZONE, TIMEZONE_OPTIONS } from "@consts";
import { DIALOG_TYPES } from "./AccountEditorConsts";
import { createDefaultAccountFields, wrapAccountRow, unwrapFieldValues } from "./AccountEditor.utils";
import { USER_PERMISSIONS } from "@hyro/dashboard-permissions";
import useAppStore from "../../../stores/appStore";
import { useFetchDashboardSettings } from "src/queries/hooks/settings";
import dayjs from "dayjs";
import { Button } from "@mui/material";
import { COLUMNS, FILTERS } from "./AccountManagement.consts";
import HyroTableContainer from "@hyro-components/hyro-table-container/HyroTableContainer";

const initialDialogState = {
  open: false,
  type: "",
  error: false,
  errorText: "",
  accountData: createDefaultAccountFields()
};

const AccountManagement = () => {
  const getAccounts = useAppStore(getAccountsAdminSelector);
  const createAccount = useAppStore(createAccountSelector);
  const editAccount = useAppStore(editAccountSelector);
  const deleteAccount = useAppStore(deleteAccountSelector);
  const error = useAppStore(accountManagementErrorSelector);
  const errorText = useAppStore(accountManagementErrorTextSelector);
  const setError = useAppStore(setAccountManagementErrorSelector);
  const rawAccounts = useAppStore(managementAccountsSelector);
  const dashboardSettings = useFetchDashboardSettings()?.data;
  const orderedAccounts = rawAccounts?.toSorted((a, b) => a.name.localeCompare(b.name));
  const hasPermission = useAppStore(hasPermissionSelector);
  const createAccountPermission = hasPermission(
    USER_PERMISSIONS.ACCOUNTS.WRITE
  );
  const [accountEditorDialog, setAccountEditorDialog]
    = useState(initialDialogState);

  useEffect(() => {
    if (error) {
      setAccountEditorDialog((prev) => ({ ...prev, error, errorText }));
    }

  }, [error, errorText]);

  useEffect(() => {
    if (!error) {
      setAccountEditorDialog(initialDialogState);
    }

  }, [rawAccounts]);

  useEffect(() => {
    getAccounts();
  }, [getAccounts]);

  const handleAccountEditorDialogClose = () => {
    setAccountEditorDialog(initialDialogState);
    setError(null);
  };

  const handleAccountEditorConfirmation = (data) => {
    const flat = unwrapFieldValues(data);
    if (accountEditorDialog.type === DIALOG_TYPES.create) {
      createAccount({ name: flat.name, timezone: flat.timezone });
    }
    else if (accountEditorDialog.type === DIALOG_TYPES.edit) {
      editAccount({
        id: data.id,
        name: flat.name,
        accountId: flat.accountId,
        timezone: flat.timezone,
        sftpIpList: flat.sftpIpList,
        publicKey: flat.publicKey
      });
    }
    else if (accountEditorDialog.type === DIALOG_TYPES.delete) {
      deleteAccount({ id: data.id });
    }
  };

  const handleResetError = () => {
    setAccountEditorDialog({ ...accountEditorDialog, error: false });
    setError(null);
  };

  const cols = [
    { field: COLUMNS.name.id, headerName: COLUMNS.name.label },
    { field: COLUMNS.accountId.id, headerName: COLUMNS.accountId.label },
    { field: COLUMNS.creator.id, headerName: COLUMNS.creator.label },
    { field: COLUMNS.createdAt.id, headerName: COLUMNS.createdAt.label, renderCell: ({ row }) => dayjs(new Date(row.createdAt)).format("MM/DD/YYYY HH:ss a") },
    { field: COLUMNS.timezone.id, headerName: COLUMNS.timezone.label, width: 250, renderCell: ({ row }) => {
      const timezoneKey = row.timezone || DEFAULT_TIMEZONE;
      const timezoneOption = TIMEZONE_OPTIONS.find(option => option.key === timezoneKey);
      return timezoneOption?.display_key || timezoneKey;
    } },
    { field: COLUMNS.twilioId.id, headerName: COLUMNS.twilioId.label }
  ];

  if (!rawAccounts) return <HyroLoader />;

  const createNewAccountButton = createAccountPermission && (
    <Button
      data-testid="account-management-create-new-account-button"
      startIcon={<AddIcon />}
      disabled={dashboardSettings?.disableChanges}
      color="primary"
      size="medium"
      variant="contained"
      onClick={() => {
        setAccountEditorDialog({
          ...accountEditorDialog,
          open: true,
          type: DIALOG_TYPES.create
        });
      }}
    >
      Create New Account
    </Button>
  );

  return (
    <>
      <HyroTableContainer
        data={orderedAccounts || []}
        header={{
          title: "Accounts",
          showDownloadButton: false,
          children: createNewAccountButton
        }}
        filters={{
          initialFilters: FILTERS
        }}
        columns={cols}
        onRowClick={({ row }) => {
          window.open(
            `${window.location.protocol}//${window.location.host}/${row.accountId}`
          );
        }}
        rowActions={
          createAccountPermission && !dashboardSettings?.disableChanges
            ? [
              {
                id: "edit",
                display: "Settings",
                run: (accountData) => {
                  setAccountEditorDialog({
                    open: true,
                    type: DIALOG_TYPES.edit,
                    accountData: wrapAccountRow(accountData, "edit")
                  });
                }
              },
              {
                id: "delete",
                display: "Delete account",
                run: (accountData) => {
                  setAccountEditorDialog({
                    open: true,
                    type: DIALOG_TYPES.delete,
                    accountData: wrapAccountRow(accountData, "delete")
                  });
                }
              }
            ]
            : null
        }
        testId="account-management"
      />
      {accountEditorDialog.open && accountEditorDialog.type === DIALOG_TYPES.edit && (
        <AccountEditorDrawer
          {...accountEditorDialog}
          onResetError={handleResetError}
          onSubmit={handleAccountEditorConfirmation}
          onClose={handleAccountEditorDialogClose}
        />
      )}
      {accountEditorDialog.open && accountEditorDialog.type !== DIALOG_TYPES.edit && (
        <AccountEditorDialog
          {...accountEditorDialog}
          onResetError={handleResetError}
          onConfirm={handleAccountEditorConfirmation}
          onClose={handleAccountEditorDialogClose}
        />
      )}
    </>
  );
};

export default AccountManagement;
